// Libs
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
// Hooks
import useOrderApi from 'core/hooks/useCoreApi';
// Components
import {
	Wrapper,
	Divider,
	PersonIcon,
	Span,
	LogoIcon,
	Button,
} from 'components';
import { ApiServiceV2 } from 'services';
// Utils
import { VirtualizedList } from 'screens/Utils';
import orderBy from 'utils/orderBy';
import {
	getTotalBonifications,
	getTotalDiscounts,
	getTotalProduction,
	getTotalToPay,
	groupResumeOrders,
} from 'screens/Billings/utils';
import { coinsUtils } from 'utils/libs';
// selectors
import { selectProfile } from 'screens/Login/selectors';
// Actions
import { sendToast } from 'screens/Dashboard/actions';
import { getPaymentSignatureCapability } from 'core/ElementViewBuilder/views/Billing/Payment/paymentSummaryCapabilities';

function GetResumeItem({ concept, value, color }) {
	const theme = useTheme();

	return (
		<Wrapper padding='5px' justifyContent='space-between' width='95%'>
			<Span fontSize='l' color={color || theme.fonts.colors.concept}>
				{concept}
			</Span>
			<Span fontSize='l' color={color || theme.fonts.colors.value}>
				{value}
			</Span>
		</Wrapper>
	);
}
const VirtualizedSection = ({
	cardId = '',
	dividerTitle,
	targetRef,
	rowHeight,
	spinTitle = '',
	rows,
	groupOrders = null,
	results,
	paymentOrders,
	state,
	profile,
}) => (
	<>
		<Divider color='gray'>{dividerTitle}</Divider>
		<Wrapper flexDirection='column' width='100%'>
			<VirtualizedList
				cardId={cardId}
				listStyle={{
					height: document.documentElement.clientHeight * 0.5,
					// width: targetRef?.current ? targetRef?.current.offsetWidth : 0,
					width: 500,
				}}
				rowHeight={rowHeight}
				spinTitle={spinTitle}
				rows={rows}
				customProps={{
					coinSymbol: state.coinSymbol,
					orders: paymentOrders,
					groupOrders,
					profile,
				}}
			/>
			<GetResumeItem concept='Subtotal' value={results.subTotal} />
			<GetResumeItem concept='IVA' value={results.iva} />
		</Wrapper>
	</>
);

const ResumenSection = ({
	prodAmounts,
	bonusAmounts,
	discountsAmounts,
	iva,
	totalToPay,
	theme,
}) => {
	return (
		<>
			<Divider color='gray'>Resumen</Divider>
			<Wrapper flexDirection='column' width='100%'>
				<GetResumeItem concept='Producción' value={prodAmounts.prodTotal} />
				<GetResumeItem
					concept='Bonificación'
					value={`+ ${bonusAmounts.bonusTotal}`}
				/>
				<GetResumeItem
					concept='Descuentos'
					value={`- ${discountsAmounts.discTotal}`}
				/>
				<GetResumeItem concept='IVA' value={`${iva}%`} />
				<GetResumeItem
					concept='Total a Pagar'
					value={totalToPay}
					color={theme.fonts.colors.emphasis_1}
				/>
			</Wrapper>
		</>
	);
};

const SignedSeccion = ({ signed, profile, signedUsers, theme }) => {
	orderBy(signed, 'objectDate', {
		objectKey: 'created_at',
		dateFormat: profile?.user?.settings?.date_format,
	});

	return (
		<>
			<Divider color='gray'>Firmas</Divider>
			<Wrapper
				width='100%'
				margin='20px 0 10px 0'
				borderRadius='20px'
				backgroundColor={theme.colors.secondary[800]}
				overflow='auto'
			>
				{signed.map((sign, idx) => {
					const user = signedUsers.find(user => user.id === sign.user_id);
					if (!user) return null;

					let style = { color: 'rgba(255, 255, 255, 0.6)' };

					if (sign.paid) style = { color: 'green' };
					else if (sign.released) style = { color: 'yellow' };
					else if (sign.agreed) style = { color: 'deepskyblue' };

					return (
						user && (
							<Wrapper key={idx} padding='0px'>
								<PersonIcon style={style} />
								<Wrapper
									margin='0 10px 10px 0'
									padding='0px'
									flexDirection='column'
									alignItems='baseline'
								>
									<Span style={style} margin='0 10px'>
										{user.name} ({sign.payment_id})
									</Span>
									<Span margin='0 10px' color='cadetblue'>
										{sign.created_at}
									</Span>
								</Wrapper>
							</Wrapper>
						)
					);
				})}
			</Wrapper>
		</>
	);
};

const FooterSeccion = ({
	state,
	handdleSignedTransaction,
	signatureActionLabel,
}) => {
	return (
		<Wrapper width='100%' justifyContent='flex-end'>
			{state.isLoading ? (
				<LogoIcon button spin />
			) : (
				<Button mode='primary' onClick={handdleSignedTransaction}>
					{signatureActionLabel}
				</Button>
			)}
		</Wrapper>
	);
};

const ResumeBody = ({
	payment = {},
	coinId,
	paymentOrders = [],
	paymentLines = [],
	cleanModal,
	modeviewGroupOrders,
	refreshMainData,
}) => {
	if (!Object.values(payment).length > 0) return <></>;

	const targetRef = useRef();

	const [state, _setState] = useState({
		isLoading: false,
		signedUsers: [],
		coinSymbol: {},

		bonifications: [],
		discounts: [],
	});

	const setState = newState =>
		_setState(current => ({ ...current, ...newState }));

	const theme = useTheme();
	const profile = useSelector(selectProfile);

	const prodAmounts = getTotalProduction(
		paymentOrders,
		payment.iva,
		state.coinSymbol,
	);
	const bonusAmounts = getTotalBonifications(
		state.bonifications,
		payment.iva,
		state.coinSymbol,
	);
	const discountsAmounts = getTotalDiscounts(
		state.discounts,
		payment.iva,
		state.coinSymbol,
	);

	const totalToPay = getTotalToPay(
		prodAmounts.productionTotal,
		bonusAmounts.bonificationTotal,
		discountsAmounts.discountTotal,
		state.coinSymbol,
	);

	const { getCoinsData } = useOrderApi();

	const signatureCapability = getPaymentSignatureCapability({
		profile,
		payment,
	});

	useEffect(() => {
		const signedUserIds =
			(payment.signed && payment.signed.map(signed => signed.user_id)) || [];

		ApiServiceV2.put({
			url: `/users/getUsers`,
			data: { usersId: signedUserIds },
		})
			.then(({ users }) => {
				setState({ signedUsers: users });
			})
			.catch(err => {
				sendToast({ message: err.message, type: err.type });
			});
	}, []);

	useEffect(() => {
		setState({
			bonifications: paymentLines.filter(line => line.action === 'credit'),
			discounts: paymentLines.filter(line => line.action === 'debit'),
		});
	}, [paymentLines]);

	useEffect(() => {
		getCoinsData().then(coinsData => {
			const coinSymbol = coinsUtils.getDataByCoinId(
				coinId,
				'symbol',
				coinsData,
			);

			setState({
				coinSymbol,
			});
		});
	}, [coinId]);

	const handdleSignedTransaction = () => {
		if (!signatureCapability.canSign) return;

		ApiServiceV2.put({
			url: `/payments/sign`,
			data: { payment_id: payment.id },
		});

		cleanModal();
		refreshMainData();
	};

	return (
		<Wrapper padding='0' width='100%' flexDirection='column' overflow='auto'>
			<Wrapper
				elementRef={targetRef}
				width='100%'
				flexDirection='column'
				justifyContent='space-between'
			>
				{/* PRODUCTION */}
				{paymentOrders.length > 0 && (
					<VirtualizedSection
						cardId='paymentOrderResumeCard'
						dividerTitle={`Producción (${paymentOrders.length})`}
						targetRef={targetRef}
						rowHeight={85}
						spinTitle={''}
						rows={
							modeviewGroupOrders
								? groupResumeOrders(paymentOrders)
								: paymentOrders
						}
						groupOrders={modeviewGroupOrders}
						results={{
							subTotal: prodAmounts.prod,
							iva: prodAmounts.prodIva,
						}}
						paymentOrders={paymentOrders}
						state={state}
						profile={profile}
					/>
				)}

				{/* BONIFICATION */}
				{state.bonifications.length > 0 && (
					<VirtualizedSection
						cardId='paymentLineResumeCard'
						dividerTitle={`Bonificación (${state.bonifications.length})`}
						rowHeight={135}
						spinTitle={'Consultando registros...'}
						rows={state.bonifications}
						results={{
							subTotal: bonusAmounts.bonus,
							iva: bonusAmounts.bonusIva,
						}}
						paymentOrders={paymentOrders}
						state={state}
						profile={profile}
					/>
				)}

				{/* DISCOUNTS */}
				{state.discounts.length > 0 && (
					<VirtualizedSection
						cardId='paymentLineResumeCard'
						dividerTitle={`Descuentos (${state.discounts.length})`}
						rowHeight={135}
						spinTitle={'Consultando registros...'}
						rows={state.discounts}
						results={{
							subTotal: discountsAmounts.disc,
							iva: discountsAmounts.discIva,
						}}
						paymentOrders={paymentOrders}
						state={state}
						profile={profile}
					/>
				)}

				{/* RESUME */}
				<ResumenSection
					prodAmounts={prodAmounts}
					bonusAmounts={bonusAmounts}
					discountsAmounts={discountsAmounts}
					iva={payment.iva}
					totalToPay={totalToPay}
					theme={theme}
				/>

				{/* SIGNED */}
				<SignedSeccion
					signed={payment.signed || []}
					profile={profile}
					signedUsers={state.signedUsers}
					theme={theme}
				/>

				{signatureCapability.canSign && (
					<FooterSeccion
						state={state}
						handdleSignedTransaction={handdleSignedTransaction}
						signatureActionLabel={signatureCapability.actionLabel}
					/>
				)}
			</Wrapper>
		</Wrapper>
	);
};

export default ResumeBody;
